import { useEffect, useRef } from "react";
import { onNotify, onReconnected, subscribe, unsubscribe, type RealtimeEvent } from "./connection";

type Options = {
  /** Called for every pushed event while subscribed. */
  onEvent?: (event: RealtimeEvent) => void;
  /** Called after an automatic reconnect, once the group has been re-joined (use to backfill). */
  onReconnect?: () => void;
};

/**
 * Subscribe to a hub group for the lifetime of the component. Re-subscribes and runs the backfill
 * callback after a reconnect. Pass a null group to subscribe to nothing (e.g. before an id loads).
 */
export function useRealtimeGroup(group: string | null, options: Options): void {
  const onEventRef = useRef(options.onEvent);
  const onReconnectRef = useRef(options.onReconnect);
  onEventRef.current = options.onEvent;
  onReconnectRef.current = options.onReconnect;

  useEffect(() => {
    if (!group) {
      return;
    }

    const offNotify = onNotify((event) => onEventRef.current?.(event));
    const offReconnected = onReconnected(() => {
      void subscribe(group).then(() => onReconnectRef.current?.());
    });
    void subscribe(group);

    return () => {
      offNotify();
      offReconnected();
      void unsubscribe(group);
    };
  }, [group]);
}
